import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AdminHTTPService } from '@services/datasources/admin-http-service';
import { FeatureService } from '@services/features/feature.service';
import { LocalStorageKeys, StorageService } from '@services/storage/storage.service';
import { UserInfoDto } from 'app/@types/dto/user-info-dto.model';
import { BehaviorSubject, filter, Subject, takeUntil } from 'rxjs';

interface UserInfoListEntry extends UserInfoDto {
    index: number;
    selected: boolean;
    fullname: string;
}
@Injectable({
    providedIn: 'root',
})
export class AdminDataSyncService implements OnDestroy {
    private destroy$ = new Subject<void>();
    private isDataLoaded = false;

    constructor(
        private AdminHttpService: AdminHTTPService,
        private featureService: FeatureService,
        private router: Router,
        private storage: StorageService,
    ) {
        // The admin console selection only lives in the BehaviorSubjects below, which are
        // wiped by the hard window.location.reload() that "take control" triggers
        // (FeatureService.impersonate). Restore it from session storage and keep it in sync.
        this.restorePersistedSelection();
        this.persistSelectionOnChange();

        this.router.events
            .pipe(
                filter((e) => e instanceof NavigationEnd),
                takeUntil(this.destroy$),
            )
            .subscribe(() => {
                if (
                    this.featureService.hasUiFeatures(['admin_admin'], false) ||
                    this.featureService.hasUiFeatures(['admin_admin'], true)
                ) {
                    this.getData(
                        this.dedicatedAdmins.getValue() !== undefined && this.dedicatedAdmins.getValue().length > 0,
                    );
                    this.isDataLoaded = true;
                }
            });
    }
    //dedicatedAdmin Signals
    public udpateListSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public updateTitleSubject: BehaviorSubject<string | undefined> = new BehaviorSubject<string | undefined>(undefined);
    public currentSelectedOrgId: BehaviorSubject<string | undefined> = new BehaviorSubject<string | undefined>(
        undefined,
    );

    public dedicatedAdmins: BehaviorSubject<UserInfoListEntry[]> = new BehaviorSubject<UserInfoListEntry[]>([]);

    //admin Signals
    public udpateDedicatedAdminListSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public currentSelectedDedicatedAdminId: BehaviorSubject<string | undefined> = new BehaviorSubject<
        string | undefined
    >(undefined);
    public updateDedicatedAdminNameSubject: BehaviorSubject<string | undefined> = new BehaviorSubject<
        string | undefined
    >(undefined);

    public loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public resetSelection() {
        // Emitting undefined also clears the persisted selection (see persistSelectionOnChange()).
        this.updateTitleSubject.next(undefined);
        this.currentSelectedOrgId.next(undefined);
        this.updateDedicatedAdminNameSubject.next(undefined);
        this.currentSelectedDedicatedAdminId.next(undefined);
        this.udpateListSubject.next(true);
        this.udpateDedicatedAdminListSubject.next(true);
        this.loading.next(false);
    }
    getData(hideLoader?: boolean) {
        if (hideLoader === undefined || hideLoader === false) {
            this.loading.next(true);
        }
        this.AdminHttpService.getDedicatedAdminList().subscribe({
            next: (adminResponse) => {
                if (!adminResponse || adminResponse.length === 0) {
                    console.warn('No dedicated admins found in response');
                    this.dedicatedAdmins.next([]);
                    this.loading.next(false);
                    return;
                }

                const admins: UserInfoListEntry[] = adminResponse.map((p, i) => {
                    const currentSelectedDAdminId = this.currentSelectedDedicatedAdminId.getValue();
                    return {
                        ...p,
                        fullname: `${p.firstname} ${p.lastname}`,
                        uid: p.uid ?? '',
                        index: i,
                        selected: currentSelectedDAdminId === p.uid,
                    } as UserInfoListEntry;
                });

                const sortedAdmins = admins.sort((a, b) => {
                    return a.fullname.localeCompare(b.fullname);
                });

                // Default to the first admin when nothing is selected or the persisted
                // selection no longer exists, before publishing the list so subscribers
                // (header dropdown, organisation list) always resolve a valid selection.
                const selectedAdminId = this.currentSelectedDedicatedAdminId.getValue();
                const selectionStillExists =
                    !!selectedAdminId && sortedAdmins.some((admin) => admin.uid === selectedAdminId);
                if (!selectionStillExists && sortedAdmins.length > 0) {
                    this.currentSelectedDedicatedAdminId.next(sortedAdmins[0].uid);
                }

                this.dedicatedAdmins.next(sortedAdmins);

                this.loading.next(false);
            },
            error: (error) => {
                console.error('Error loading dedicated admins:', error);
                this.dedicatedAdmins.next([]);
                this.loading.next(false);
            },
        });
    }

    /**
     * Restore the admin-console selection (dedicated admin + organisation) persisted before the
     * page reload triggered by "take control" (FeatureService.impersonate). Session-scoped so it
     * survives the reload but never leaks into a fresh tab or another user's session.
     */
    private restorePersistedSelection(): void {
        this.currentSelectedDedicatedAdminId.next(
            this.storage.getGlobalItem<string>(LocalStorageKeys.selectedDedicatedAdminId, 1, undefined, 'session'),
        );
        this.currentSelectedOrgId.next(
            this.storage.getGlobalItem<string>(LocalStorageKeys.selectedOrgId, 1, undefined, 'session'),
        );
    }

    /**
     * Persist every selection change so it survives the reload. Centralised here so all existing
     * `.next()` call sites (and resetSelection()) stay persistence-agnostic; emitting undefined
     * clears the stored value.
     */
    private persistSelectionOnChange(): void {
        this.currentSelectedDedicatedAdminId
            .pipe(takeUntil(this.destroy$))
            .subscribe((id) => this.persistSelection(LocalStorageKeys.selectedDedicatedAdminId, id));
        this.currentSelectedOrgId
            .pipe(takeUntil(this.destroy$))
            .subscribe((id) => this.persistSelection(LocalStorageKeys.selectedOrgId, id));
    }

    private persistSelection(key: LocalStorageKeys, id: string | undefined): void {
        if (id) {
            this.storage.setGlobalItem<string>(key, id, 1, undefined, 'session');
        } else {
            this.storage.removeItem(key, 'session');
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
